/**
 * Tsuki's client-side interface settings.
 *
 * Purely local (localStorage via the `persistedState` Vuex module) — no API, no schema change.
 * That is deliberate: these describe how the app should look on *this* device, and they have to
 * be right on the first frame, before any settings request comes back.
 *
 * Komga's own "UI settings" screens stay where they are; those are server-side per-user client
 * settings (poster stretch, blur unread, OAuth2) and are shared across every device.
 *
 * The set is modelled on Plezy's appearance settings — see .claude/docs/plezy-ux.md §9.3.
 */

import {ACCENT_DEFAULT} from '@/types/ui-accents'

export enum StartPage {
  DASHBOARD = 'dashboard',
  LIBRARIES = 'libraries',
  COLLECTIONS = 'collections',
  READLISTS = 'readlists',
}

/** Primary sidebar entries, in the order HomeView declares them. */
export const NAV_KEYS = ['home', 'series', 'books', 'collections', 'readlists'] as const
export type NavKey = typeof NAV_KEYS[number]

/** Plezy's ViewMode: a wall of covers, or rows carrying the metadata that decides. */
export enum ViewMode {
  GRID = 'grid',
  LIST = 'list',
}

export interface UiSettings {
  /** Which view the app opens on. Plezy calls it the startup section. */
  startPage: StartPage
  /** Labels under the icons of the bottom navigation. */
  bottomNavLabels: boolean
  /** Title and subtitle under a poster. Off gives a pure cover wall. */
  cardTitles: boolean
  /** The unread counter in the corner of a poster. */
  cardUnreadBadge: boolean
  /** The read-progress bar along the bottom edge of a poster. */
  cardProgressBar: boolean
  /** Hover lift and card transitions. Off is also what prefers-reduced-motion gets. */
  cardAnimations: boolean
  /** Keep the first card of a list sharp even when unread posters are blurred. */
  blurKeepFirst: boolean
  /** Group a read list by series instead of listing every single volume. */
  readListGroupBySeries: boolean
  /** Sticky letter bar above library grids. */
  showAlphaBar: boolean
  /** Covers in a grid, or rows with metadata. */
  viewMode: ViewMode
  /** The book number in front of a title ("3 - Chapter"). Plezy: showEpisodeNumberOnCards. */
  showBookNumber: boolean
  /** Plezy's hideSpoilers: keep the description of something unread out of sight. */
  hideUnreadSummaries: boolean
  /** Blur radius in px for unread posters. Only has an effect with the account's blur on. */
  blurStrength: number
  /**
   * Offer the administrative actions — edit, delete, scan, analyse, empty trash.
   *
   * An admin account carries a second UI on top of the reading one, and most of the time an
   * admin is just reading. Purely a display switch: the roles are untouched and the server still
   * enforces them, so nothing here grants or removes a permission. Non-admins never see the row.
   */
  showAdminActions: boolean

  // --- sidebar ---
  /** Which primary entries the sidebar shows, in the order it shows them. */
  navOrder: string[]
  /** Entries switched off. Kept separate from the order so re-enabling restores the position. */
  navHidden: string[]
  /** The LIBRARIES block. */
  showSidebarLibraries: boolean
  /** The admin SERVER block. Ignored for non-admins, who never see it anyway. */
  showSidebarServer: boolean
  /** The version line at the very bottom. */
  showSidebarVersion: boolean
  /** Open the sidebar as an icon rail on every start instead of remembering the last state. */
  sidebarStartCollapsed: boolean

  // --- appearance ---
  /** Accent colour key, see types/ui-accents.ts. */
  accent: string

  // --- browsing ---
  /** Items per page in the browsing grids; 0 keeps Komga's own default. */
  pageSize: number
}

export const UI_DEFAULTS: UiSettings = {
  startPage: StartPage.DASHBOARD,
  bottomNavLabels: true,
  cardTitles: true,
  cardUnreadBadge: true,
  cardProgressBar: true,
  cardAnimations: true,
  // off by default: it changes what an existing library looks like, and the plain blur is the
  // behaviour people already know
  blurKeepFirst: false,
  readListGroupBySeries: true,
  showAlphaBar: true,
  viewMode: ViewMode.GRID,
  showBookNumber: true,
  hideUnreadSummaries: false,
  // 5px is what the blur has always been; the setting starts where people already are
  blurStrength: 5,
  showAdminActions: true,

  navOrder: [...NAV_KEYS],
  navHidden: [],
  showSidebarLibraries: true,
  showSidebarServer: true,
  showSidebarVersion: true,
  sidebarStartCollapsed: false,

  accent: ACCENT_DEFAULT,

  pageSize: 0,
}
